import { chunk } from 'lodash';
import {
	lmsAxiosClient,
	lmsDirectoryAxiosClient
} from 'LearningManager/service/lmsClient';
import ASSIGNMENT_SOURCES_API from 'Services/Api/AssignmentSources/Api';
import {
	AssignmentSource,
	ListAssignmentSourcesResponse
} from 'Services/Api/AssignmentSources/Types';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import {
	DirectoryUser,
	UsersPageResponse
} from 'Services/Api/Directory/Types';
import { RowStatus } from './Table/TrainingTable.types';

const DIRECTORY_USERS_CHUNK = 100;
const ASSIGNMENT_SOURCES_PAGE_SIZE = 1000;

export const statusFromCompletion = (completion: number): RowStatus => {
	if (completion >= 100) return RowStatus.Complete;
	if (completion <= 0) return RowStatus.NotStarted;
	return RowStatus.Incomplete;
};

export const directoryUserName = (user?: DirectoryUser): string | undefined => {
	if (!user) return undefined;
	const name = [user.lastName, user.firstName].filter(Boolean).join(', ');
	return name || user.email || user.login;
};

export const fetchDirectoryUsers = async (
	ids: string[]
): Promise<DirectoryUser[]> => {
	const pages = await Promise.all(
		chunk([...new Set(ids)], DIRECTORY_USERS_CHUNK).map(async (batch) => {
			const { data } = await lmsDirectoryAxiosClient.get<
				UsersPageResponse | DirectoryUser[]
			>(DIRECTORY_API.USERS, {
				params: {
					id: batch.join(','),
					include_group_tree: false,
					page: 1,
					limit: batch.length
				}
			});
			return Array.isArray(data) ? data : data?.data ?? [];
		})
	);
	return ([] as DirectoryUser[]).concat(...pages);
};

const assignmentSourcesByTenant = new Map<
	number | undefined,
	Promise<AssignmentSource[]>
>();

export const fetchReportAssignmentSources = (
	tenantId?: number,
	refresh = false
): Promise<AssignmentSource[]> => {
	const cached = assignmentSourcesByTenant.get(tenantId);
	if (cached && !refresh) return cached;

	const request = lmsAxiosClient
		.get<ListAssignmentSourcesResponse>(
			ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCES,
			{
				params: {
					offset: 0,
					page_size: ASSIGNMENT_SOURCES_PAGE_SIZE,
					...(tenantId ? { tenant_id: tenantId } : {})
				}
			}
		)
		.then(({ data }) => data.data)
		.catch((error) => {
			assignmentSourcesByTenant.delete(tenantId);
			throw error;
		});

	assignmentSourcesByTenant.set(tenantId, request);
	return request;
};
